import React, { useEffect, useRef, useState } from 'react';
import { Col, Select } from 'antd';
import { ScreenWrapperComponent } from 'components/layouts/ScreenWrapperComponent';
import { StatsWrapper } from './components/StatsWrapper';
import { ButtonsWrapper } from './components/ButtonsWrapper';
import MessageSentIcon from 'assets/icons/messageSentIcon.svg';
import UniqueRecipientsIcon from 'assets/icons/uniqueRecipientsIcon.svg';
import TotalRevenueIcon from 'assets/icons/totalRevenueIcon.svg';
import CurrentAudienceIcon from 'assets/icons/currentAudienceIcon.svg';
import CreditBalanceIcon from 'assets/icons/creditBalanceIcon.svg';
import CreateCampaignIcon from 'assets/icons/createCampaignIcon.svg';
import ScheduledCampaignsIcon from 'assets/icons/scheduledCampaignsIcon.svg';
import CampaignAnalyticsIcon from 'assets/icons/campaignAnalyticsIcon.svg';
import { parseDataBusiness } from 'helpers/dropdown-data';
import { useGetBusinessStats } from 'hooks/useGetBusinessStats';
import { useCreateCampaignStoreActions } from 'store/CreateCampaignStore';
import { HOME_SCREEN } from 'constants/enums/home-screen';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';
import { ResultPage } from 'components/ResultPage';
import { useGetAggregatedAnalytics } from 'hooks/useGetAggregatedAnalytics';
import { ClearableSelectComponent } from 'baseComponents/ClearableSelectComponent';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { BusinessType, useGetBusinessesList } from 'hooks/useGetBusinessesList';

import 'screens/HomeScreen/homeScreen.scss';

export const HomeScreen: React.FC = () => {
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessType | null>(
    null
  );
  const [selectedTemplateType, setSelectedTemplateType] =
    useState<string>('sms');

  const searchBusinesses = useRef<any[]>([]);

  const {
    isLoading: aggregatedAnalyticsLoading,
    error: aggregatedAnalyticsError,
    data: aggregatedAnalyticsData,
    refetch: aggregatedAnalyticsRefetch,
  } = useGetAggregatedAnalytics({
    channel: selectedTemplateType,
  });
  const { isLoading: businessesListLoading, data: businessesListData } =
    useGetBusinessesList();

  const { isLoading: isUserDataLoading, data: userData } = useGetCurrentUser();

  const showBusinessDropdown =
    userData?.role_code === 'moderator' || userData?.role_code === 'admin';

  const {
    isLoading: businessStatsLoading,
    error: businessStatsError,
    data: businessStatsData,
  } = useGetBusinessStats(
    selectedBusiness?.id ? selectedBusiness?.id.toString() : '',
    selectedTemplateType
  );
  const { reset: clearCreateCampaignStore } = useCreateCampaignStoreActions();

  useEffect(() => {
    clearCreateCampaignStore();
    aggregatedAnalyticsRefetch();
    searchBusinesses.current = businessDropdownList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle click on businesses dropdown item
  const handleBusinessChange = (id?: number) => {
    if (id) {
      const selectedBusiness = businessesListData?.find((obj) => obj.id === id);
      setSelectedBusiness(selectedBusiness || null);
    } else {
      aggregatedAnalyticsRefetch();
      setSelectedBusiness({
        id: null,
        name: 'Show all businesses statistics',
      });
    }
  };

  // List of businesses displayed in a dropdown.
  const businessDropdownList = businessesListData
    ? parseDataBusiness(
        businessesListData as BusinessType[],
        handleBusinessChange
      )
    : [];

  // Data for displaying stats for every business
  const generateStatsInfo = () => {
    const dataSource = selectedBusiness?.id
      ? businessStatsData
      : aggregatedAnalyticsData;
    return [
      {
        statsValue: dataSource?.messages_sent,
        title: HOME_SCREEN.MESSAGES_SENT,
        iconType: MessageSentIcon,
      },
      {
        statsValue: dataSource?.confirmed_deliveries,
        title: HOME_SCREEN.CONFIRMED_DELIVERIES,
        iconType: UniqueRecipientsIcon,
      },
      {
        statsValue: dataSource?.total_revenue
          ? `£${dataSource?.total_revenue}`
          : '£0',
        title: HOME_SCREEN.TOTAL_REVENUE,
        iconType: TotalRevenueIcon,
      },
      {
        statsValue: dataSource?.current_audience,
        title: HOME_SCREEN.CURRENT_AUDIENCE,
        iconType: CurrentAudienceIcon,
      },
      {
        statsValue: dataSource?.credit_balance,
        title: HOME_SCREEN.CREDIT_BALANCE,
        iconType: CreditBalanceIcon,
        creditWarning: dataSource?.credit_warning,
      },
    ];
  };

  // Content of buttons for navigating through campaigns flow.
  const buttonsArray = [
    {
      title: 'Create campaign',
      iconName: CreateCampaignIcon,
      url: '/create-campaign',
    },
    {
      title: 'Scheduled campaigns',
      iconName: ScheduledCampaignsIcon,
      url: '/scheduled-campaigns',
    },
    {
      title: 'Campaign analytics',
      iconName: CampaignAnalyticsIcon,
      url: '/campaign-analytics',
    },
  ];

  if (businessStatsError || aggregatedAnalyticsError) {
    return <ResultPage />;
  }

  const handleSearch = (value: string) => {
    const searchResults = businessDropdownList.filter((item: any) => {
      const labelLowerCase = item.label.toLowerCase();
      const searchValueLowerCase = value.toLowerCase();
      return labelLowerCase.includes(searchValueLowerCase);
    });
    if (searchResults.length === 0) {
      searchBusinesses.current = [];
    } else if (value.length > 0) {
      searchBusinesses.current = businessDropdownList;
    } else {
      searchBusinesses.current = searchResults;
    }
  };

  return (
    <ScreenWrapperComponent>
      <div className='home__header'>
        <Col
          lg={{ pull: 3, push: 3, span: 18 }}
          md={{ pull: 1, push: 1, span: 22 }}
        >
          <div className='home__header-wrapper'>
            <div className='home__header-wrapper__dropdown-wrapper'>
              {showBusinessDropdown && (
                <ClearableSelectComponent
                  options={businessDropdownList}
                  placeholder='Select Business'
                  value={selectedBusiness?.name || 'Select Business'}
                  onChange={(business) => handleBusinessChange(business)}
                  onClear={() => setSelectedBusiness(null)}
                  showSearch
                  onSearch={(value) => handleSearch(value)}
                />
              )}
            </div>

            <Select
              className={`multi-select-dropdown`}
              allowClear={false}
              placeholder={'Campaign type'}
              onChange={(value) => setSelectedTemplateType(value)}
              value={selectedTemplateType ?? 'Select campaign type'}
              popupClassName='multi-select-popup'
              showSearch={false}
              style={{ width: 220 }}
              options={[
                {
                  label: 'SMS',
                  value: 'sms',
                },
                {
                  label: 'Push notification',
                  value: 'push',
                },
              ]}
            />
          </div>
          <div className='stats-wrapper'>
            <StatsWrapper statsElements={generateStatsInfo()} />
          </div>
        </Col>
      </div>
      <Col
        className='home-buttons-wrapper'
        lg={{ pull: 3, push: 3, span: 18 }}
        md={{ pull: 1, push: 1, span: 22 }}
      >
        <ButtonsWrapper buttonsArray={buttonsArray} />
      </Col>
      <FullScreenLoaderComponent
        spinning={
          businessStatsLoading ||
          aggregatedAnalyticsLoading ||
          isUserDataLoading ||
          businessesListLoading
        }
      />
    </ScreenWrapperComponent>
  );
};
